import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import {
  UserSelectorChoiceList,
  UserSelectorEmployee,
} from '@/shared/components/user-selector';
import { Button } from '@/shared/components/ui/Button';
import { WizardStepProps } from '../types/wizard.types';
import { TeamCollaborationIllustration } from '@/shared/components/illustrations';

import type { Employee } from '@momentum/types';
import { ComponentReturnTypeMap } from '@momentum/types';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface BaseWizardEmployeeChoiceStepProps {
  columns?: 1 | 2 | 3;
  placeholder?: string;
  showSelectAll?: boolean;
  autoAdvance?: boolean;
  employees: Employee[];
}

interface WizardEmployeeChoiceStepSingleProps
  extends BaseWizardEmployeeChoiceStepProps,
    WizardStepProps<ComponentReturnTypeMap['EmployeeSelection']> {
  multiSelect?: false;
  onChange: (value: Employee) => void;
}

interface WizardEmployeeChoiceStepMultiProps
  extends BaseWizardEmployeeChoiceStepProps,
    WizardStepProps<ComponentReturnTypeMap['MultiEmployeeSelection']> {
  multiSelect: true;
  onChange: (value: Employee[]) => void;
}

type WizardEmployeeChoiceStepProps =
  | WizardEmployeeChoiceStepSingleProps
  | WizardEmployeeChoiceStepMultiProps;

// Validation function for use in wizard flow configuration
export const validateEmployeeSelection = (
  answer: unknown,
  multiSelect = false
): string | null => {
  if (!answer) {
    return multiSelect
      ? 'Please select at least one employee'
      : 'Please select an employee';
  }

  if (!multiSelect) {
    // Single selection validation
    return null; // Single selection is valid if answer exists
  }

  // Multi-selection validation - just check that at least one is selected
  const selectedCount = Array.isArray(answer) ? answer.length : 1;

  if (selectedCount < 1) {
    return 'Please select at least one employee';
  }

  return null;
};

export const WizardEmployeeChoiceStep: React.FC<
  WizardEmployeeChoiceStepProps
> = ({
  value,
  onChange: _onChange, // Renamed to indicate it's not used in multi-select
  onNext,
  error,
  employees,
  multiSelect,
  disabled = false,
  columns = 2,
  placeholder,
  showSelectAll = true,
  autoAdvance: _autoAdvance = true, // Renamed to indicate it's not used in multi-select
  setSubmitHandler,
}) => {
  const { t } = useTranslation('userSelector');
  const searchPlaceholder = placeholder || t('searchPlaceholder');

  const me = useEmployeeContext();
  // Internal state for multi-select to prevent auto-advance
  const [internalSelection, setInternalSelection] = React.useState<Employee[]>(
    []
  );
  // Transform employees to UserSelectorEmployee format and sort by team membership
  const userSelectorEmployees: UserSelectorEmployee[] = useMemo(() => {
    if (!employees || !Array.isArray(employees)) {
      return [];
    }

    // Get current user's teamId from employees list (context doesn't include teamId)
    const currentUserTeamId =
      me.teamId ?? employees.find((emp) => emp._id === me._id)?.teamId;

    // Map employees and mark team members
    const mappedEmployees = employees.map((employee) => ({
      id: employee._id,
      firstName: employee.personalInfo.firstName,
      lastName: employee.personalInfo.lastName,
      fullName: `${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`,
      isActive: true,
      imageUrl: employee.personalInfo.imageUrl,
      role: employee.role,
      isTeamMember: currentUserTeamId
        ? employee.teamId === currentUserTeamId
        : false,
    }));

    // Sort: team members first, then others (maintain alphabetical order within each group)
    return mappedEmployees.sort((a, b) => {
      // Team members come first
      if (a.isTeamMember && !b.isTeamMember) return -1;
      if (!a.isTeamMember && b.isTeamMember) return 1;
      // Within same group, sort alphabetically by full name
      return a.fullName.localeCompare(b.fullName);
    });
  }, [employees, me._id]);

  // Initialize internal selection from value
  useEffect(() => {
    if (multiSelect && value) {
      if (Array.isArray(value)) {
        setInternalSelection(value);
      } else {
        setInternalSelection([value]);
      }
    }
  }, [multiSelect, value]);

  // Handle selection changes (both single and multiple)
  const handleSelectionChange = (selectedIds: string[]) => {
    if (disabled) return;

    // Find the full employee objects for the selected IDs
    const selectedEmployees = selectedIds
      .map((id) => employees.find((emp) => emp._id === id))
      .filter((emp): emp is Employee => emp !== undefined);

    if (multiSelect) {
      // For multi-select, only update internal state - don't call onChange yet
      setInternalSelection(selectedEmployees);
    } else {
      // Single selection - pass the first employee object and auto-advance
      const selectedEmployee = selectedEmployees[0];
      if (selectedEmployee) {
        onNext(selectedEmployee); // This will trigger wizard advancement
      }
    }
  };

  // Handle manual next for multi-select
  const handleManualNext = () => {
    if (multiSelect && internalSelection.length > 0) {
      onNext(internalSelection); // This will trigger wizard advancement
    }
  };

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  if (multiSelect) {
    typedSetSubmitHandler?.(handleManualNext);
  }

  // Get current selection IDs
  const selectedEmployeeIds = useMemo(() => {
    const currentSelection = multiSelect ? internalSelection : value;

    if (!currentSelection) return [];

    // Convert to array if not already
    const selectionArray = Array.isArray(currentSelection)
      ? currentSelection
      : [currentSelection];

    // Extract IDs from employee objects or use string IDs directly
    return selectionArray.map((item) => item._id);
  }, [value, multiSelect, internalSelection]);

  // Validation for multi-select
  const getValidationMessage = () => {
    if (!multiSelect) return null;

    const count = internalSelection.length;

    if (count < 1) {
      return t('pleaseSelectAtLeast')(1, t('employee'));
    }

    return null;
  };

  const validationError = getValidationMessage();
  const displayError = error || validationError;

  return (
    <div
      className={cn(
        'w-full mx-auto border-none space-y-6',
        displayError ? 'border-red-500' : ''
      )}
    >
      <div className="flex justify-center">
        <TeamCollaborationIllustration />
      </div>

      {/* Content */}
      <div>
        <UserSelectorChoiceList
          employees={userSelectorEmployees.filter(
            (employee) => employee.id !== me._id
          )}
          selectedEmployees={selectedEmployeeIds}
          onSelectionChange={handleSelectionChange}
          multiSelect={multiSelect}
          searchable={true}
          searchPlaceholder={searchPlaceholder}
          maxHeight="max-h-96"
          showSelectAll={showSelectAll}
          columns={columns}
          className="w-full"
        />

        {/* Multi-select specific UI */}
        {multiSelect && (
          <div className="mt-4 space-y-3">
            {/* Selection count */}
            {selectedEmployeeIds.length > 0 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{t('selectionCount')(selectedEmployeeIds.length)}</span>
              </div>
            )}

            {/* Next button — hidden when card footer handles it */}
            {!typedSetSubmitHandler && (
              <div className="flex justify-end">
                <Button
                  onClick={handleManualNext}
                  disabled={internalSelection.length < 1 || disabled}
                  className="px-6"
                >
                  {t('continue')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {displayError && (
          <p className="text-sm text-destructive mt-2">{displayError}</p>
        )}
      </div>
    </div>
  );
};
